import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listInvoices, openInvoicePdf, downloadInvoicePdf } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Plus, FileText, FileDown, Download, Receipt } from "lucide-react";
import { toast } from "sonner";
import FactuurVanuitOfferteDialog from "../components/facturen/FactuurVanuitOfferteDialog";
import VrijeFactuurDialog from "../components/facturen/VrijeFactuurDialog";
import ProFormaDialog from "../components/facturen/ProFormaDialog";
import OverviewControls from "../components/overview/OverviewControls";
import { useOverviewPreferences } from "../hooks/useOverviewPreferences";
import { applySort, naturalCompare, statusOrderComparator } from "../lib/sortOverview";

const STATUS_LABEL = {
  draft: "Concept",
  definitive: "Definitief",
  sent: "Verzonden",
  paid: "Betaald",
  cancelled: "Geannuleerd",
};
const STATUS_TONE = {
  draft:      "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  definitive: "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  sent:       "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  paid:       "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  cancelled:  "bg-[#FAF9F6] text-[#A8A7A0] border-[#E5E2DC] line-through",
};
const STATUS_OPTIONS = ["draft", "definitive", "sent", "paid", "cancelled"];
// Business workflow-order voor sortering.
const INVOICE_STATUS_ORDER = ["draft", "definitive", "sent", "paid", "cancelled"];

const TYPE_LABEL = {
  deposit: "Voorschot",
  interim: "Tussenfactuur",
  final: "Eindfactuur",
  free: "Vrij",
};
// Business type-order (voor sortering): factuurstroom van eenvoudig → complex.
const INVOICE_TYPE_ORDER = ["deposit", "interim", "final", "free"];

// v14aj — Sorteeropties Facturen. Totaal incl. blijft beschikbaar (persistent veld).
const INVOICE_SORT_OPTIONS = [
  { value: "number_desc", label: "Factuurnr aflopend" },
  { value: "number_asc", label: "Factuurnr oplopend" },
  { value: "client_asc", label: "Klant A → Z" },
  { value: "client_desc", label: "Klant Z → A" },
  { value: "date_desc", label: "Datum nieuw → oud" },
  { value: "date_asc", label: "Datum oud → nieuw" },
  { value: "type_asc", label: "Type (workflow)" },
  { value: "status_asc", label: "Status (workflow)" },
  { value: "total_desc", label: "Totaal incl. hoog → laag" },
  { value: "total_asc", label: "Totaal incl. laag → hoog" },
];

const numeric = (s) => {
  const m = String(s || "").match(/(\d+)/g);
  return m ? m.map(Number).reduce((a, b) => a * 10000 + b, 0) : 0;
};

const INVOICE_COMPARATORS = {
  number_desc: (a, b) => numeric(b.invoice_number) - numeric(a.invoice_number),
  number_asc: (a, b) => numeric(a.invoice_number) - numeric(b.invoice_number),
  client_asc: (a, b) => naturalCompare(a.client_name, b.client_name),
  client_desc: (a, b) => naturalCompare(b.client_name, a.client_name),
  date_desc: (a, b) => String(b.invoice_date || b.created_at || "").localeCompare(String(a.invoice_date || a.created_at || "")),
  date_asc: (a, b) => String(a.invoice_date || a.created_at || "").localeCompare(String(b.invoice_date || b.created_at || "")),
  type_asc: statusOrderComparator((inv) => inv.invoice_type || "free", INVOICE_TYPE_ORDER, "asc"),
  status_asc: statusOrderComparator((inv) => inv.status || "draft", INVOICE_STATUS_ORDER, "asc"),
  total_desc: (a, b) => Number(b.total_incl || 0) - Number(a.total_incl || 0),
  total_asc: (a, b) => Number(a.total_incl || 0) - Number(b.total_incl || 0),
};

const fmtEur = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" })
    .format(Number(n || 0));

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("nl-BE"); } catch { return "—"; }
};

const handleOpenPdf = async (id) => {
  try {
    await openInvoicePdf(id);
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401) toast.error("Sessie verlopen. Log opnieuw in.");
    else if (status === 404) toast.error("Factuur niet gevonden.");
    else toast.error("PDF kon niet worden geopend.");
  }
};

// v14aj — Compacte type-badge (Voorschot/Tussen/Eind/Vrij + Pro forma/Creditnota).
function InvoiceTypeBadge({ invoice }) {
  if (invoice.doc_kind === "pro_forma") {
    return <Badge variant="outline" className="text-[10px] uppercase bg-[#F0EFEA] text-[#4F4F4D] border-[#E5E2DC]">Pro forma</Badge>;
  }
  if (invoice.doc_kind === "credit_note") {
    return <Badge variant="outline" className="text-[10px] uppercase bg-[#FDF0EE] text-[#B84B45] border-[#E9C3C0]">Creditnota</Badge>;
  }
  return (
    <Badge variant="outline" className="text-xs bg-[#FAF9F6] border-[#E5E2DC]">
      {TYPE_LABEL[invoice.invoice_type] || invoice.invoice_type}
    </Badge>
  );
}

export default function Facturen() {
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [freeOpen, setFreeOpen] = useState(false);
  const [proFormaOpen, setProFormaOpen] = useState(false);
  const { sort, setSort } = useOverviewPreferences("facturen", "list", "date_desc");

  const load = async () => {
    setLoading(true);
    try {
      const [filtered, all] = await Promise.all([
        listInvoices({
          ...(status ? { status } : {}),
          ...(type ? { invoice_type: type } : {}),
        }),
        (status || type) ? listInvoices({}) : Promise.resolve(null),
      ]);
      setInvoices(filtered);
      setAllInvoices(all || filtered);
    } catch {
      toast.error("Facturen laden mislukt.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [status, type]);

  // v14aj — Auto-refresh op focus + visibility.
  useEffect(() => {
    const onFocus = () => { load(); };
    const onVis = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line
  }, [status, type]);

  const totals = useMemo(() => {
    const c = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
    allInvoices.forEach((inv) => { c[inv.status] = (c[inv.status] || 0) + 1; });
    return c;
  }, [allInvoices]);

  // v14aj — Zoek uitgebreid met klantnaam en notities. Backend-filters (status/type)
  // gebeuren serverside, tekstzoek clientside voor snelle reactie.
  const filtered = useMemo(() => {
    if (!q) return invoices;
    const ql = q.toLowerCase();
    return invoices.filter((inv) => (
      String(inv.invoice_number || "").toLowerCase().includes(ql) ||
      String(inv.quote_number_snapshot || "").toLowerCase().includes(ql) ||
      String(inv.client_name || "").toLowerCase().includes(ql) ||
      String(inv.notes || "").toLowerCase().includes(ql)
    ));
  }, [invoices, q]);

  const sorted = useMemo(() => applySort(filtered, sort, INVOICE_COMPARATORS), [filtered, sort]);

  return (
    <div className="space-y-6" data-testid="facturen-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold">EmmaMeasure</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] leading-tight">Facturen</h1>
          <p className="text-sm text-[#4F4F4D] mt-1">Voorschotten, tussentijdse en eindfacturen — automatisch uit goedgekeurde offertes of vrij aangemaakt.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setWizardOpen(true)}
            className="h-11 rounded-xl bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white font-medium"
            data-testid="facturen-nieuw-vanuit-offerte"
          >
            <FileText className="w-4 h-4 mr-1.5" /> Vanuit offerte
          </Button>
          <Button
            onClick={() => setFreeOpen(true)}
            variant="outline"
            className="h-11 rounded-xl border-[#E5E2DC] text-[#2C2C2A] hover:bg-[#F0EFEA] font-medium"
            data-testid="facturen-nieuw-vrij"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Vrije factuur
          </Button>
          <Button
            onClick={() => setProFormaOpen(true)}
            variant="outline"
            className="h-11 rounded-xl border-[#E5E2DC] text-[#2C2C2A] hover:bg-[#F0EFEA] font-medium"
            data-testid="facturen-nieuw-proforma"
          >
            <FileText className="w-4 h-4 mr-1.5" /> Pro forma
          </Button>
        </div>
      </div>

      {/* Status-chips (quick filters) */}
      <div className="flex flex-wrap gap-2 text-xs" data-testid="facturen-status-chips">
        {STATUS_OPTIONS.map((s) => {
          const active = status === s;
          return (
            <button
              key={s} type="button"
              onClick={() => setStatus(active ? "" : s)}
              aria-pressed={active}
              data-testid={`facturen-chip-${s}`}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition ${
                active ? "bg-[#2C2C2A] text-white border-[#2C2C2A]" : `${STATUS_TONE[s]} hover:bg-[#F0EFEA]`
              }`}
            >
              <span>{STATUS_LABEL[s]}</span>
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                  active ? "bg-white/25 text-white" : "bg-white text-[#4F4F4D] border border-[#E5E2DC]"
                }`}
              >
                {totals[s] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* v14aj — Uniforme control-row met zoek + type-filter + sort. */}
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-3 sm:p-4">
        <OverviewControls
          moduleKey="facturen"
          search={{ value: q, onChange: setQ, placeholder: "Zoek op factuurnr, klant, offertenr, notitie…", testId: "facturen-search" }}
          filters={[{
            id: "type",
            placeholder: "Alle types",
            value: type || "all",
            onChange: (v) => setType(v === "all" ? "" : v),
            options: [
              { value: "all", label: "Alle types" },
              ...Object.entries(TYPE_LABEL).map(([v, l]) => ({ value: v, label: l })),
            ],
            testId: "facturen-type-filter",
          }]}
          sort={{ value: sort, onChange: setSort, options: INVOICE_SORT_OPTIONS }}
        />
      </div>

      {/* Desktop lijst */}
      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="facturen-list-view">
        <div
          className="hidden md:grid gap-3 px-4 py-2 bg-[#FAF9F6] border-b border-[#E5E2DC] text-[11px] font-semibold uppercase tracking-wide text-[#6E6E6C]"
          style={{ gridTemplateColumns: "130px minmax(0,1.3fr) 100px 120px 110px 130px 130px 140px" }}
          data-testid="facturen-list-header"
        >
          <div>Factuurnr</div>
          <div>Klant</div>
          <div>Datum</div>
          <div>Type</div>
          <div>Offerte</div>
          <div>Status</div>
          <div className="text-right">Totaal incl.</div>
          <div className="text-right">Acties</div>
        </div>

        {loading && <div className="text-center text-[#5D7259] py-6">Laden…</div>}
        {!loading && sorted.length === 0 && (
          <div className="py-10 text-center space-y-2">
            <div className="w-12 h-12 rounded-2xl bg-[#F0EFEA] flex items-center justify-center mx-auto">
              <Receipt className="w-5 h-5 text-[#5D7259]" />
            </div>
            <p className="text-sm text-[#4F4F4D]">Nog geen facturen.</p>
            <p className="text-xs text-[#A8A7A0]">Start met een factuur uit een goedgekeurde offerte of maak een vrije factuur.</p>
          </div>
        )}

        <ul className="divide-y divide-[#E5E2DC]">
          {sorted.map((inv) => (
            <li key={inv.id} className="hover:bg-[#FAF9F6]" data-testid={`factuur-row-${inv.invoice_number || inv.id}`}>
              {/* Desktop grid */}
              <div
                className="hidden md:grid gap-3 px-4 py-3 items-center text-sm"
                style={{ gridTemplateColumns: "130px minmax(0,1.3fr) 100px 120px 110px 130px 130px 140px" }}
              >
                <div className="min-w-0 truncate font-mono text-xs text-[#4F4F4D]" title={inv.invoice_number || ""} data-testid={`row-number-${inv.id}`}>
                  {inv.invoice_number || <span className="italic text-[#A8A7A0]">— concept —</span>}
                </div>
                <div className="min-w-0 truncate text-[#D96C4A] font-medium" title={inv.client_name || ""} data-testid={`row-client-${inv.id}`}>
                  {inv.client_name || "—"}
                </div>
                <div className="text-[#4F4F4D] tabular-nums text-xs" data-testid={`row-date-${inv.id}`}>
                  {formatDate(inv.invoice_date || inv.created_at)}
                </div>
                <div data-testid={`row-type-${inv.id}`}>
                  <InvoiceTypeBadge invoice={inv} />
                </div>
                <div className="min-w-0 truncate font-mono text-xs text-[#4F4F4D]" title={inv.quote_number_snapshot || ""} data-testid={`row-quote-${inv.id}`}>
                  {inv.quote_number_snapshot || "—"}
                </div>
                <div data-testid={`row-status-${inv.id}`}>
                  <Badge variant="outline" className={`text-xs ${STATUS_TONE[inv.status]}`}>{STATUS_LABEL[inv.status]}</Badge>
                </div>
                <div className="text-right font-medium text-[#2C2C2A] tabular-nums" data-testid={`row-total-${inv.id}`}>
                  {fmtEur(inv.total_incl)}
                </div>
                <div className="flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" className="h-9 w-9 p-0" title="PDF openen" onClick={() => handleOpenPdf(inv.id)} data-testid={`factuur-pdf-${inv.invoice_number || inv.id}`}>
                    <FileDown className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-9 w-9 p-0" title="PDF downloaden" onClick={() => { try { downloadInvoicePdf(inv.id); } catch (e) { toast.error("Download mislukt."); } }} data-testid={`factuur-download-${inv.invoice_number || inv.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button asChild size="sm" variant="ghost" className="h-9 px-3 text-xs" data-testid={`factuur-open-${inv.invoice_number || inv.id}`}>
                    <Link to={`/facturen/${inv.id}`}>Openen</Link>
                  </Button>
                </div>
              </div>

              {/* Mobile card */}
              <div className="md:hidden px-4 py-3 space-y-2" data-testid={`factuur-card-${inv.invoice_number || inv.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-[#4F4F4D]">
                      {inv.invoice_number || <span className="italic text-[#A8A7A0]">— concept —</span>}
                    </div>
                    <div className="text-[#D96C4A] font-medium truncate">{inv.client_name || "—"}</div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      <InvoiceTypeBadge invoice={inv} />
                      <Badge variant="outline" className={`text-xs ${STATUS_TONE[inv.status]}`}>{STATUS_LABEL[inv.status]}</Badge>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-medium text-[#2C2C2A] tabular-nums">{fmtEur(inv.total_incl)}</div>
                    <div className="text-[10px] text-[#4F4F4D] tabular-nums">{formatDate(inv.invoice_date || inv.created_at)}</div>
                  </div>
                </div>
                {inv.quote_number_snapshot && (
                  <div className="text-xs text-[#4F4F4D] font-mono">→ {inv.quote_number_snapshot}</div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" className="h-9 rounded-lg gap-1 text-xs flex-1" onClick={() => handleOpenPdf(inv.id)} data-testid={`factuur-pdf-mobile-${inv.invoice_number || inv.id}`}>
                    <FileDown className="w-3.5 h-3.5" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" className="h-9 w-9 rounded-lg p-0 shrink-0" onClick={() => { try { downloadInvoicePdf(inv.id); } catch (e) { toast.error("Download mislukt."); } }} data-testid={`factuur-download-mobile-${inv.invoice_number || inv.id}`} aria-label="PDF downloaden">
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <Button asChild size="sm" className="h-9 rounded-lg bg-[#D96C4A] hover:bg-[#C05A3C] text-white text-xs flex-1" data-testid={`factuur-open-mobile-${inv.invoice_number || inv.id}`}>
                    <Link to={`/facturen/${inv.id}`}>Openen</Link>
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <FactuurVanuitOfferteDialog
        open={wizardOpen}
        onOpenChange={(v) => { setWizardOpen(v); if (!v) load(); }}
      />
      <VrijeFactuurDialog
        open={freeOpen}
        onOpenChange={(v) => { setFreeOpen(v); if (!v) load(); }}
      />
      <ProFormaDialog
        open={proFormaOpen}
        onOpenChange={(v) => { setProFormaOpen(v); if (!v) load(); }}
      />
    </div>
  );
}
